import Modal from 'modules/common/components/Modal';
import { DISCOUNT_VALUE_TYPES } from '../../constants';
import discountsStrings from '../../strings';
import type { DiscountActionType, DiscountItem } from '../../types/discount.types';
import { useEditOfferForm } from './useEditOfferForm';
import {
  EditOfferDiscountRow,
  EditOfferGrid,
  InfoBanner,
  InlineError,
  ModalBodyContent,
  ModalField,
  ModalInput,
  ModalLabel,
  ModalSelect,
} from '../../views/Discounts.style';

interface EditOfferModalProps {
  isOpen: boolean;
  discount: DiscountItem | null;
  onClose: () => void;
  actionType: DiscountActionType;
  isSaving: boolean;
  title?: string;
}

const EditOfferModal = ({
  isOpen,
  discount,
  onClose,
  actionType,
  isSaving,
  title = discountsStrings.editOfferTitle,
}: EditOfferModalProps) => {
  const isActive = discount?.status === 'Active';
  const today = new Date().toISOString().split('T')[0];

  const { register, errors, handleFormSubmit } = useEditOfferForm({
    isOpen,
    discount,
    actionType,
  });

  return (
    <Modal
      toggle={isOpen}
      title={title}
      onClose={onClose}
      onCancelHandler={onClose}
      cancelActionLabel={discountsStrings.cancelBtn}
      onConfirmHandler={handleFormSubmit}
      confirmActionLabel={discountsStrings.saveEditsBtn}
      confirmActionLoading={isSaving}
      closeOnClickOutside={false}
      size="md"
    >
      <ModalBodyContent>
        <EditOfferGrid>
          <input type="hidden" {...register('originalPrice', { valueAsNumber: true })} />
          <ModalField>
            <ModalLabel>{discountsStrings.originalPriceFieldLabel}</ModalLabel>
            <ModalInput
              value={discount ? `${discount.originalPrice} ${discountsStrings.currency}` : ''}
              readOnly
            />
          </ModalField>

          <ModalField>
            <ModalLabel>{discountsStrings.discountFieldLabel}</ModalLabel>
            <EditOfferDiscountRow>
              <ModalInput
                type="number"
                min="0"
                step="any"
                {...register('discountValue')}
              />
              <ModalSelect {...register('discountType')}>
                <option value={DISCOUNT_VALUE_TYPES.Amount}>{discountsStrings.currency}</option>
                <option value={DISCOUNT_VALUE_TYPES.Percent}>%</option>
              </ModalSelect>
            </EditOfferDiscountRow>
            {errors.discountValue && <InlineError>{errors.discountValue.message}</InlineError>}
          </ModalField>

          <ModalField>
            <ModalLabel>{discountsStrings.fromLabel}</ModalLabel>
            <ModalInput type="date" min={today} {...register('startDate')} disabled={isActive} />
            {errors.startDate && <InlineError>{errors.startDate.message}</InlineError>}
          </ModalField>

          <ModalField>
            <ModalLabel>{discountsStrings.toLabel}</ModalLabel>
            <ModalInput type="date" min={today} {...register('endDate')} />
            {errors.endDate && <InlineError>{errors.endDate.message}</InlineError>}
          </ModalField>
        </EditOfferGrid>

        <InfoBanner>{discountsStrings.discountInfoBanner}</InfoBanner>
      </ModalBodyContent>
    </Modal>
  );
};

export default EditOfferModal;
